import React from 'react';
import { Box, Card, CardContent, CardHeader, Skeleton, Stack, Tooltip, Typography, useTheme } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';
import EmptyState from './EmptyState';
import type { FunnelStepResult } from '../utils/computeFunnel';

import { opsColors } from '../../../../theme/opsTokens';

interface FunnelWidgetProps {
  title: string;
  icon?: React.ReactNode;
  steps: FunnelStepResult[];
  loading?: boolean;
  /** Maximum value to scale bars against. Defaults to the first step's count. */
  helperText?: string;
}

export default function FunnelWidget({
  title,
  icon,
  steps,
  loading = false,
  helperText,
}: FunnelWidgetProps) {
  const { t } = useTranslation('admin');
  const max = steps[0]?.count ?? 0;
  const totalEntered = max;

  return (
    <Card variant="outlined" sx={{ height: '100%' }}>
      <CardHeader
        title={
          <Stack direction="row" spacing={1} alignItems="center">
            {icon}
            <Typography variant="subtitle1" fontWeight={700}>{title}</Typography>
          </Stack>
        }
        subheader={
          helperText ?? (totalEntered > 0
            ? t('analytics.funnel.entered', { count: totalEntered })
            : undefined)
        }
        subheaderTypographyProps={{ variant: 'caption' }}
        sx={{ pb: 0 }}
      />
      <CardContent sx={{ pt: 1 }}>
        {loading ? (
          <Stack spacing={1}>
            {[0, 1, 2].map((i) => <Skeleton key={i} variant="rectangular" height={36} />)}
          </Stack>
        ) : steps.length === 0 || max === 0 ? (
          <EmptyState
            compact
            title={t('analytics.funnel.noData')}
            body={t('analytics.funnel.noDataBody')}
          />
        ) : (
          <Stack spacing={1.5}>
            {steps.map((step, idx) => {
              const widthPct = max > 0 ? (step.count / max) * 100 : 0;
              const barColor = idx === 0
                ? opsColors.brand.primary
                : alpha(opsColors.brand.primary, Math.max(0.35, 1 - idx * 0.18));
              return (
                <Stack key={step.id} spacing={0.25}>
                  <Stack direction="row" justifyContent="space-between" alignItems="baseline">
                    <Typography variant="body2" fontWeight={600}>
                      {step.label}
                    </Typography>
                    <Stack direction="row" spacing={1} alignItems="baseline">
                      <Typography variant="body2" fontWeight={700} sx={{ fontVariantNumeric: 'tabular-nums' }}>
                        {step.count.toLocaleString()}
                      </Typography>
                      <Tooltip title={idx === 0 ? t('analytics.funnel.entryStep') : t('analytics.funnel.conversionFromPrevious')} arrow>
                        <Typography variant="caption" color="text.secondary">
                          {(step.rateFromStart * 100).toFixed(0)}%
                        </Typography>
                      </Tooltip>
                    </Stack>
                  </Stack>
                  <Box
                    sx={{
                      height: 10,
                      borderRadius: 1,
                      backgroundColor: alpha(opsColors.neutral.ink, 0.06),
                      overflow: 'hidden',
                    }}
                  >
                    <Box
                      sx={{
                        height: '100%',
                        width: `${widthPct}%`,
                        backgroundColor: barColor,
                        transition: 'width 280ms ease-out',
                      }}
                    />
                  </Box>
                  {idx > 0 && step.dropOff > 0 && (
                    <Typography variant="caption" color="text.disabled">
                      {t('analytics.funnel.droppedOff', {
                        count: step.dropOff,
                        pct: ((1 - step.rateFromPrevious) * 100).toFixed(0),
                      })}
                    </Typography>
                  )}
                </Stack>
              );
            })}
          </Stack>
        )}
      </CardContent>
    </Card>
  );
}
